import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { blueGreyMui, blueMui, greyMui, rydeColors } from "@ryde/theme";

import { ASK_AI_TRANSCRIPT_MIN_HEIGHT_PX } from "../investigation.constants";

/**
 * The Ask-AI tab: intro, the scrolling transcript, then the composer pinned below it.
 *
 * The `.inner-conversation-wrapper` / `.assistant-message-block` / `.user-message` /
 * `.streaming-cursor` classes belong to the shared `AiAdvisorChatRenderer`; they are restyled here
 * because the renderer is reused as-is and only its surroundings change for the narrow panel.
 */
export const AskAiSectionStyled = styled.section.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-ask-ai",
})`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 10px;
  min-height: 0;

  .ask-ai-intro {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.5;
    color: ${greyMui._600};
  }

  .ask-ai-transcript {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: ${ASK_AI_TRANSCRIPT_MIN_HEIGHT_PX}px;
    border: 1px solid ${greyMui._200};
    border-radius: 8px;
    background: ${rydeColors.white};
    overflow: hidden;
  }

  .inner-conversation-wrapper {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: start;
    gap: 12px;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px;
    font-size: 13px;
    line-height: 1.6;
    color: ${blueGreyMui._900};
  }

  .inner-conversation-wrapper > ul {
    padding-inline-start: 18px;
  }

  .assistant-message-block {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    align-self: stretch;
    overflow-wrap: anywhere;
  }

  .user-message {
    align-self: flex-end;
    max-width: 85%;
    padding: 6px 10px;
    border-radius: 10px;
    border: 1px solid ${blueMui._100};
    background: ${blueMui._50};
    overflow-wrap: anywhere;
  }

  .streaming-cursor {
    display: inline-block;
    width: 2px;
    height: 1em;
    margin-inline-start: 2px;
    vertical-align: text-bottom;
    background: ${greyMui._600};
  }

  @media (prefers-reduced-motion: no-preference) {
    .streaming-cursor {
      animation: ask-ai-blink 0.8s step-end infinite;
    }
  }

  @keyframes ask-ai-blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }

  .ask-ai-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${greyMui._600};
  }

  .ask-ai-composer {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 6px;
  }

  .ask-ai-input-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid ${greyMui._300};
    border-radius: 8px;
    background: ${rydeColors.white};
    transition: border-color 0.15s ease;
  }

  .ask-ai-input-row:focus-within {
    border-color: ${blueMui._500};
  }

  /* The glyph itself is not mirrored by the RTL plugin, so flip it with the layout */
  html[dir="rtl"] & .ask-ai-send svg {
    transform: scaleX(-1);
  }
`;

/** Starter questions shown while the transcript is still empty. */
export const AskAiSuggestionsStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-ask-ai-suggestions",
})`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;

  .suggestions-title {
    font-size: 12px;
    font-weight: 600;
    color: ${greyMui._600};
  }

  .suggestion {
    padding: 7px 10px;
    border-radius: 8px;
    border: 1px solid ${greyMui._300};
    background: ${rydeColors.white};
    font: inherit;
    font-size: 12.5px;
    color: ${blueGreyMui._900};
    text-align: start;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .suggestion:hover {
    border-color: ${blueMui._300};
    background: ${blueMui._50};
  }

  .suggestion:focus-visible {
    outline: 2px solid ${blueMui._600};
    outline-offset: 2px;
  }
`;
